import { BLUE_100, BLUE_600, GRAY_800, GRAY_900 } from 'constants/styling/theme'
import { SELECTION_RING_SX, useSelectionStyle } from '../SelectionStyle'
import { Box, SxProps, Theme, Typography } from '@mui/material'
import React, { MouseEvent, ReactNode } from 'react'

import { BorderBoxWrapper } from '../BorderBoxWrapper'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Stack from '@mui/material/Stack'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** Whether the button is toggled or not. The toggled check icon will be based on this. */
  isToggled?: boolean
  /** Whether the button is selected or not. The blue border and background effect will be based on it. */
  isSelected?: boolean
  /** Element that should be used as toggle icon (defaults to checkmark icon) */
  toggleIcon?: ReactNode
  /** The button label displayed beside the icon  */
  label: string | ReactNode
  /** The icon displayed on the left side */
  icon?: ReactNode
  /** Onclick action to be called */
  onClick?: (e: MouseEvent) => unknown
  /** Custom style for box wrapper */
  sx?: SxProps<Theme>
  /** Typography variant for the label */
  typographyVariant?: 'text-sm' | 'text-md' | 'text-lg'
}

/**
 * @component ToggleButton with custom icon, label and click action. Display toggled status with checked icon on the button top-right corner.
 * @example
 * <ToggleButton
 *  isToggled={true}
 *  isSelected={false}
 *  label="Ongoing"
 *  icon=<Icon />
 *  onClick={e => console.log(e)}
 *  typographyVariant='text-md'
 * />
 */
export const ToggleButton: React.FC<Props> = ({
  isToggled = false,
  isSelected = false,
  toggleIcon = <CheckCircleIcon sx={{ color: GRAY_800 }} />,
  icon,
  label,
  onClick,
  sx,
  typographyVariant = 'text-sm',
}) => {
  const isRing = useSelectionStyle() === 'ring'
  return (
    <BorderBoxWrapper
      elevation="xs"
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '1.2rem',
        padding: '.8rem 1.6rem',
        cursor: 'pointer',
        height: '4.8rem',
        width: '17rem',
        background: isSelected && !isRing ? BLUE_100 : 'white',
        border: isSelected ? `1px solid ${isRing ? SELECTION_RING_SX.borderColor : BLUE_600}` : 'inherit',
        ...(isSelected && isRing && { boxShadow: SELECTION_RING_SX.boxShadow }),
        ...sx
      }}
      onClick={e => onClick?.(e)}
    >

      {icon &&
        <Stack
          fontSize={2}
          marginRight={.8}
          textAlign="center"
          justifyContent="center"
          alignItems="center"
          color={GRAY_800}
        >
          {icon}
        </Stack>
      }

      {isToggled &&
        <Box
          position="absolute"
          top={-6}
          right={-6}
        >
          {toggleIcon}
        </Box>
      }

      <Typography variant={typographyVariant} fontWeight="medium" color={GRAY_900}>
        {label}
      </Typography>

    </BorderBoxWrapper>
  )
}
